import React, { useEffect, useMemo, useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress,
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
    TextField, Typography,
} from '@mui/material';
import RegisterNotes, { humanizeCode } from './RegisterNotes';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { StatutoryRegisterService } from '../../services/statutoryRegister.service';
import { IWageComplianceRegister, IWageComplianceRow } from '../../interfaces/statutoryRegister.interface';
import { useToast } from '../../context/ToastContext';
import { dash, money, num, triggerDownload } from './shared';

interface Props {
    institutionId: string;
}

const ROSTER_PAGE_SIZE_OPTIONS = [25, 50, 100];

/** 0 = a flag other than NO_GRADE_ASSIGNED, 1 = NO_GRADE_ASSIGNED only, 2 = clean. Mirrors the backend order. */
export const flagRank = (row: IWageComplianceRow): number => {
    if (row.flags.length === 0) return 2;
    return row.flags.some(f => f.code !== 'NO_GRADE_ASSIGNED') ? 0 : 1;
};

const flagColor = (code: string): 'error' | 'warning' | 'default' => {
    if (code === 'BELOW_GRADE_FLOOR' || code === 'BELOW_MINIMUM_WAGE') return 'error';
    if (code === 'INACTIVE_GRADE') return 'warning';
    return 'default';
};

/**
 * Seventh statutory register tab: wage compliance (decision note 2026-09-08 wage-grades REV 2,
 * D3/D5). No period parameters -- this is a point-in-time check of CURRENT active salary
 * structures, mirroring the service's own framing. R1/C2: {@code catalogConfigured === false}
 * renders an honest "not configured" state instead of a zero-flag sheet that could be mistaken
 * for a clean compliance result.
 */
const WageComplianceTab: React.FC<Props> = ({ institutionId }) => {
    const toast = useToast();
    const [register, setRegister] = useState<IWageComplianceRegister | null>(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [rosterPage, setRosterPage] = useState(0);
    const [rosterPageSize, setRosterPageSize] = useState(ROSTER_PAGE_SIZE_OPTIONS[0]);
    const [search, setSearch] = useState('');

    const load = async () => {
        setLoading(true);
        setRosterPage(0);
        try {
            const data = await StatutoryRegisterService.getWageComplianceRegister(institutionId);
            setRegister(data);
        } catch (e) {
            setRegister(null);
            toast.showApiError(e, 'Failed to load wage compliance register');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const blob = await StatutoryRegisterService.downloadWageComplianceRegisterXlsx(institutionId);
            triggerDownload(blob, 'wage-compliance-register.xlsx');
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadPdf = async () => {
        setDownloadingPdf(true);
        try {
            const blob = await StatutoryRegisterService.downloadWageComplianceRegisterPdf(institutionId);
            triggerDownload(blob, 'wage-compliance-register.pdf');
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloadingPdf(false);
        }
    };

    // Flagged rows first (stable, so the backend's name order holds inside each group), then filtered
    // by name/code.
    const visibleRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        return (register?.rows ?? [])
            .map((row, i) => ({ row, i }))
            .sort((a, b) => flagRank(a.row) - flagRank(b.row) || a.i - b.i)
            .map(x => x.row)
            .filter(r => !q
                || (r.employeeName ?? '').toLowerCase().includes(q)
                || (r.employeeCode ?? '').toLowerCase().includes(q));
    }, [register, search]);
    const pagedRows = visibleRows.slice(rosterPage * rosterPageSize, rosterPage * rosterPageSize + rosterPageSize);

    return (
        <Box data-help-page="statutory.wageCompliance.page">
            <Box data-help-section="statutory.wageCompliance.toolbar" display="flex" gap={2} alignItems="center" mb={2} flexWrap="wrap">
                <Button data-help="statutory.wageCompliance.refresh" variant="outlined" size="small" onClick={load} disabled={loading}>Refresh</Button>
                <Button data-help="statutory.wageCompliance.downloadXlsx"
                    variant="contained" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleDownload} disabled={!register || downloading}
                    data-testid="wage-compliance-download"
                >
                    Download XLSX
                </Button>
                <Button data-help="statutory.wageCompliance.downloadPdf"
                    variant="outlined" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleDownloadPdf} disabled={!register || downloadingPdf}
                    data-testid="wage-compliance-download-pdf"
                >
                    Download PDF
                </Button>
            </Box>

            {loading && <CircularProgress size={24} />}

            {!loading && register && register.notice && (
                <Alert severity="warning" sx={{ mb: 2 }} data-testid="wage-compliance-min-wage-notice" data-help="statutory.wageCompliance.notice">
                    {register.notice}
                </Alert>
            )}

            {!loading && register && !register.catalogConfigured && (
                <Alert severity="info" sx={{ mb: 2 }} data-testid="wage-compliance-not-configured" data-help="statutory.wageCompliance.notConfigured">
                    No wage grades are configured for this institution, so zero flags here does NOT
                    mean wages are compliant: no minimum-wage check has been run. Go to Wage Grade
                    Management to load the 2023 gazette defaults or add grades manually.
                </Alert>
            )}

            {!loading && register && (
                <>
                    {/* R7/R13 bundle convention: caveats ALWAYS render. */}
                    {/* The notice already has its own Alert above; the exports keep it in caveats. */}
                    <RegisterNotes
                        notes={register.caveats.filter(c => c !== register.notice)}
                        testId="wage-compliance-caveats"
                    />

                    {register.catalog.length > 0 && (
                        <Accordion sx={{ mb: 2 }} data-testid="wage-compliance-catalog-section" data-help-section="statutory.wageCompliance.catalog">
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography>Wage Grade Catalog ({register.catalog.length})</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell data-help="statutory.wageCompliance.catalog.col.code">Code</TableCell>
                                            <TableCell data-help="statutory.wageCompliance.catalog.col.name">Display Name</TableCell>
                                            <TableCell align="right" data-help="statutory.wageCompliance.catalog.col.minimum">Minimum Gross Wage</TableCell>
                                            <TableCell data-help="statutory.wageCompliance.catalog.col.active">Active</TableCell>
                                            <TableCell data-help="statutory.wageCompliance.catalog.col.editStatus">Edit Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {register.catalog.map(g => (
                                            <TableRow key={g.code}>
                                                <TableCell>{g.code}</TableCell>
                                                <TableCell>{g.displayName}</TableCell>
                                                <TableCell align="right">{money(g.minimumGrossWage)}</TableCell>
                                                <TableCell>{g.isActive ? 'Active' : 'Inactive'}</TableCell>
                                                <TableCell>
                                                    <Chip size="small" label={humanizeCode(g.editStatus)}
                                                          color={g.editStatus === 'EDITED' ? 'warning' : 'default'} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    <Box data-help-section="statutory.wageCompliance.filter" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                        <TextField
                            data-help="statutory.wageCompliance.search"
                            size="small" label="Search employee name or code" value={search}
                            onChange={e => { setSearch(e.target.value); setRosterPage(0); }}
                            sx={{ flex: '1 1 260px', maxWidth: 420 }}
                            inputProps={{ 'data-testid': 'wage-compliance-search' }}
                        />
                    </Box>

                    <TableContainer component={Paper} data-testid="wage-compliance-roster-table" data-help-section="statutory.wageCompliance.table">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="statutory.wageCompliance.col.employee">Employee</TableCell>
                                    <TableCell data-help="statutory.wageCompliance.col.code">Code</TableCell>
                                    <TableCell data-help="statutory.wageCompliance.col.grade">Grade</TableCell>
                                    <TableCell align="right" data-help="statutory.wageCompliance.col.gross">Gross</TableCell>
                                    <TableCell align="right" data-help="statutory.wageCompliance.col.gradeFloor">Grade Floor</TableCell>
                                    <TableCell data-help="statutory.wageCompliance.col.flags">Flags</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pagedRows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <Typography color="text.secondary">
                                                {search.trim() ? 'No employees match your search.' : 'No employees with an active salary structure.'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {pagedRows.map(row => (
                                    <TableRow key={row.userId} hover>
                                        <TableCell>{dash(row.employeeName)}</TableCell>
                                        <TableCell>{dash(row.employeeCode)}</TableCell>
                                        <TableCell>{dash(row.wageGradeDisplayName)}</TableCell>
                                        <TableCell align="right">{money(row.gross)}</TableCell>
                                        <TableCell align="right">
                                            {row.gradeFloor != null ? money(row.gradeFloor) : '—'}
                                        </TableCell>
                                        <TableCell>
                                            {row.flags.length === 0 && '—'}
                                            {row.flags.map((f, i) => (
                                                <Chip key={i} size="small" sx={{ mr: 0.5, mb: 0.5 }}
                                                      color={flagColor(f.code)}
                                                      label={f.shortfallAmount != null
                                                          ? `${humanizeCode(f.code)} (৳${money(f.shortfallAmount)})`
                                                          : humanizeCode(f.code)} />
                                            ))}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={visibleRows.length}
                        page={rosterPage}
                        onPageChange={(_, p) => setRosterPage(p)}
                        rowsPerPage={rosterPageSize}
                        onRowsPerPageChange={e => { setRosterPageSize(parseInt(e.target.value, 10)); setRosterPage(0); }}
                        rowsPerPageOptions={ROSTER_PAGE_SIZE_OPTIONS}
                    />

                    <Accordion sx={{ mt: 2 }} data-testid="wage-compliance-disclosures-section" data-help-section="statutory.wageCompliance.disclosures">
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Disclosures</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <TableCell data-help="statutory.wageCompliance.disclosure.liveMembership">Live Membership Count</TableCell>
                                        <TableCell align="right">{num(register.grandTotal.liveMembershipCount)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell data-help="statutory.wageCompliance.disclosure.withSalary">With Active Salary Count</TableCell>
                                        <TableCell align="right">{num(register.grandTotal.withActiveSalaryCount)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell data-help="statutory.wageCompliance.disclosure.noSalary">No Active Salary Count</TableCell>
                                        <TableCell align="right">{num(register.grandTotal.noActiveSalaryCount)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell data-help="statutory.wageCompliance.disclosure.noProfile">Members Without Profile</TableCell>
                                        <TableCell align="right">{num(register.grandTotal.membersWithoutProfile)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell data-help="statutory.wageCompliance.disclosure.belowFloor">Below Grade Floor Count</TableCell>
                                        <TableCell align="right" data-testid="wage-compliance-below-floor-count">
                                            {num(register.grandTotal.belowGradeFloorCount)}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell data-help="statutory.wageCompliance.disclosure.belowMinimum">Below Minimum Wage Count</TableCell>
                                        <TableCell align="right" data-testid="wage-compliance-below-minimum-count">
                                            {num(register.grandTotal.belowMinimumWageCount ?? 0)}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell data-help="statutory.wageCompliance.disclosure.noGrade">No Grade Assigned Count</TableCell>
                                        <TableCell align="right">{num(register.grandTotal.noGradeAssignedCount)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell data-help="statutory.wageCompliance.disclosure.inactiveGrade">Inactive Grade Count</TableCell>
                                        <TableCell align="right">{num(register.grandTotal.inactiveGradeCount)}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </AccordionDetails>
                    </Accordion>
                </>
            )}
        </Box>
    );
};

export default WageComplianceTab;
